import { useId, useRef } from 'react';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { useFloatingPanel } from './useFloatingPanel';
import { useFrame, MathType } from '@react-three/fiber';
import type { HtmlProps } from '@react-three/drei/web/Html';
import { Property } from 'csstype';

export type FloatingPanelProps = Omit<HtmlProps, 'id'> & {
  id?: string;
  position: MathType<THREE.Vector3>;
  markerColor?: Property.Color;
  dotSize?: number;
  lineWidth?: number;
  offset?: [number, number];
  margin?: number;
};

export const FloatingPanel: React.FC<FloatingPanelProps> = ({
  id,
  children,
  position,
  scale,
  rotation,
  markerColor = 'black',
  distanceFactor,
  dotSize = 15,
  lineWidth = 3,
  offset,
  margin,
  zIndexRange,
  ...restProps
}) => {
  const internalId = useId();
  const { setElement, node } = useFloatingPanel(id ?? internalId, {
    margin,
    offset,
  });

  const anchorRef = useRef<HTMLDivElement>(null);
  //const panelRef  = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);

  useFrame(() => {
    if (!anchorRef.current || !node || !lineRef.current) return;

    const height = Math.abs(node.position[1]);
    lineRef.current.style.top = `${-height}px`;
    lineRef.current.style.height = `${height}px`;
  });

  return (
    <group position={position} scale={scale} rotation={rotation}>
      {/* anchor dot */}
      <Html
        center
        {...restProps}
        distanceFactor={distanceFactor}
        zIndexRange={zIndexRange ? [zIndexRange[0] * 0.5, zIndexRange[1] * 0.5] : undefined}
      >
        <div
          ref={anchorRef}
          style={{
            position: 'relative',
            width: dotSize,
            height: dotSize,
            borderRadius: dotSize,
            backgroundColor: markerColor,
          }}
        >
          <div
            ref={lineRef}
            style={{
              position: 'absolute',
              width: lineWidth,
              height: 0,
              transform: 'translateX(-50%)',
              left: '50%',
              backgroundColor: markerColor,
            }}
          />
        </div>
      </Html>

      <Html center {...restProps} distanceFactor={distanceFactor} zIndexRange={zIndexRange}>
        <div ref={setElement}>{children}</div>
      </Html>
    </group>
  );
};
